import { useEffect, useMemo, useState } from "react";
import {
    Badge,
    Box,
    Collapse,
    List,
    ListItemButton,
    Tooltip,
    Typography,
    type Theme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, LayoutDashboard } from "lucide-react";
import type { ReactElement } from "react";

import { getVisibleModuleNavTree } from "../../features/auth/access";
import { useAppSelector } from "../../app/hooks";
import {
    findActiveModuleId,
    findActiveNavBranchIds,
    isSelectedNavHref,
    navHrefBasePath,
} from "../../routes/moduleNavigation.utils";
import type { BusinessModuleId, ModuleNavLeaf } from "../../routes/moduleNavigation";
import { fetchMessagesUnreadCount } from "../../lib/api/messages";
import { routePaths } from "../../routes/navigation";
import { scheduleWorkspacePrefetch } from "../../routes/modulePrefetch";
import { moduleNavHeaderSx, moduleNavIconSx, moduleNavIcons, moduleNavThemes, navIcons } from "./appShell.constants";

const expandedModulesStorageKey = "app-shell-nav-expanded";

function collapsedNavTooltip(label: string, show: boolean, node: ReactElement) {
    if (!show) {
        return node;
    }

    return (
        <Tooltip title={label} placement="right" arrow enterDelay={200}>
            {node}
        </Tooltip>
    );
}

function firstLeafHref(nodes: ModuleNavLeaf[]): string | null {
    for (const node of nodes) {
        if (node.href) {
            return node.href;
        }

        if (node.children?.length) {
            const nested = firstLeafHref(node.children);

            if (nested) {
                return nested;
            }
        }
    }

    return null;
}

function readStoredExpandedModules(): Set<BusinessModuleId> {
    if (typeof window === "undefined") {
        return new Set(["global"]);
    }

    try {
        const raw = window.localStorage.getItem(expandedModulesStorageKey);

        if (!raw) {
            return new Set(["global"]);
        }

        const parsed = JSON.parse(raw) as BusinessModuleId[];

        return new Set(parsed);
    } catch {
        return new Set(["global"]);
    }
}

function persistExpandedModules(modules: Set<BusinessModuleId>) {
    window.localStorage.setItem(
        expandedModulesStorageKey,
        JSON.stringify([...modules]),
    );
}

interface AppShellNavTreeProps {
    desktopCollapsed: boolean;
    isMobile: boolean;
    pathname: string;
    search: string;
    onNavigate: (href: string) => void;
    theme: Theme;
}

export default function AppShellNavTree({
    desktopCollapsed,
    isMobile,
    pathname,
    search,
    onNavigate,
    theme,
}: AppShellNavTreeProps) {
    const user = useAppSelector((state) => state.auth.user);
    const unreadQuery = useQuery({
        queryKey: ["messages-unread-count"],
        queryFn: fetchMessagesUnreadCount,
        enabled: Boolean(user),
        refetchInterval: 30_000,
        retry: 1,
    });
    const unreadMessages = unreadQuery.data?.unreadCount ?? 0;
    const visibleModules = getVisibleModuleNavTree(user);
    const showCollapsedTooltips = desktopCollapsed && !isMobile;
    const activeModuleId = findActiveModuleId(pathname, search);
    const activeBranchIds = useMemo(
        () => findActiveNavBranchIds(pathname, search),
        [pathname, search],
    );

    const [expandedModules, setExpandedModules] = useState<Set<BusinessModuleId>>(
        () => readStoredExpandedModules(),
    );
    const [expandedBranches, setExpandedBranches] = useState<Set<string>>(() => new Set());

    useEffect(() => {
        if (!activeModuleId) {
            return;
        }

        setExpandedModules((current) => {
            if (current.has(activeModuleId)) {
                return current;
            }

            const next = new Set(current);
            next.add(activeModuleId);
            persistExpandedModules(next);

            return next;
        });
    }, [activeModuleId]);

    useEffect(() => {
        if (activeBranchIds.length === 0) {
            return;
        }

        setExpandedBranches((current) => {
            const next = new Set(current);
            let changed = false;

            for (const branchId of activeBranchIds) {
                if (!next.has(branchId)) {
                    next.add(branchId);
                    changed = true;
                }
            }

            return changed ? next : current;
        });
    }, [activeBranchIds]);

    const toggleModule = (moduleId: BusinessModuleId) => {
        setExpandedModules((current) => {
            const next = new Set(current);
            if (next.has(moduleId)) {
                next.delete(moduleId);
            } else {
                next.add(moduleId);
            }
            persistExpandedModules(next);

            return next;
        });
    };

    const toggleBranch = (branchId: string) => {
        setExpandedBranches((current) => {
            const next = new Set(current);

            if (next.has(branchId)) {
                next.delete(branchId);
            } else {
                next.add(branchId);
            }

            return next;
        });
    };

    const childPadding = (depth: number) => {
        if (showCollapsedTooltips) {
            return 1.5;
        }

        if (depth <= 1) {
            return 4.5;
        }

        return 4.5 + (depth - 1) * 2;
    };

    const renderNavNode = (node: ModuleNavLeaf, depth = 1) => {
        if (node.children?.length) {
            const isExpanded = expandedBranches.has(node.id);
            const isActiveBranch = activeBranchIds.includes(node.id);

            return (
                <Box key={node.id} component="span" sx={{ display: "block" }}>
                    <ListItemButton
                        className="app-shell__navButton app-shell__navButton--child"
                        selected={isActiveBranch}
                        onClick={() => toggleBranch(node.id)}
                        sx={{
                            borderRadius: 2,
                            minHeight: 44,
                            pl: childPadding(depth),
                            pr: 2,
                            gap: 1,
                        }}
                    >
                        <Typography sx={{ fontWeight: 700, flex: 1, fontSize: "0.88rem" }}>
                            {node.label}
                        </Typography>
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </ListItemButton>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List disablePadding sx={{ display: "grid", gap: 0.5 }}>
                            {node.children.map((child) => renderNavNode(child, depth + 1))}
                        </List>
                    </Collapse>
                </Box>
            );
        }

        if (!node.href) {
            return null;
        }

        const basePath = navHrefBasePath(node.href);
        const Icon = navIcons[basePath as keyof typeof navIcons] || LayoutDashboard;
        const selected = isSelectedNavHref(pathname, search, node.href);
        const nested = depth > 1;

        return (
            <Box key={node.id} component="span" sx={{ display: "block" }}>
                {collapsedNavTooltip(
                    node.label,
                    showCollapsedTooltips,
                    <ListItemButton
                        selected={selected}
                        className={nested ? "app-shell__navButton app-shell__navButton--child" : "app-shell__navButton"}
                        onMouseEnter={() => scheduleWorkspacePrefetch(basePath)}
                        onFocus={() => scheduleWorkspacePrefetch(basePath)}
                        onClick={() => onNavigate(node.href!)}
                        sx={{
                            borderRadius: 2,
                            minHeight: nested ? 44 : 56,
                            pl: childPadding(depth),
                            pr: showCollapsedTooltips ? 1.5 : 2,
                            justifyContent: showCollapsedTooltips ? "center" : "flex-start",
                            alignItems: "center",
                            gap: showCollapsedTooltips ? 0 : 1.25,
                            transition: theme.transitions.create(["background-color", "padding"], {
                                duration: 250,
                                easing: theme.transitions.easing.easeInOut,
                            }),
                        }}
                    >
                        <Box
                            className="app-shell__navIcon"
                            sx={{
                                minWidth: nested ? 30 : 36,
                                width: nested ? 30 : 36,
                                height: nested ? 30 : 36,
                            }}
                        >
                            <Icon size={nested ? 16 : 18} />
                        </Box>
                        {(!desktopCollapsed || isMobile) && (
                            node.href === routePaths.messages && unreadMessages > 0 ? (
                                <Badge color="error" badgeContent={unreadMessages > 99 ? "99+" : unreadMessages} sx={{ "& .MuiBadge-badge": { fontWeight: 800 } }}>
                                    <Typography sx={{ fontWeight: nested ? 600 : 800, fontSize: nested ? "0.88rem" : undefined, pr: 1 }}>
                                        {node.label}
                                    </Typography>
                                </Badge>
                            ) : (
                                <Typography sx={{ fontWeight: nested ? 600 : 800, fontSize: nested ? "0.88rem" : undefined }}>
                                    {node.label}
                                </Typography>
                            )
                        )}
                    </ListItemButton>,
                )}
            </Box>
        );
    };

    if (showCollapsedTooltips) {
        return (
            <>
                {visibleModules.map((module) => {
                    const ModuleIcon = moduleNavIcons[module.id];
                    const moduleTheme = moduleNavThemes[module.id];
                    const firstHref = firstLeafHref(module.children);
                    const isActive = module.id === activeModuleId;

                    if (!firstHref) {
                        return null;
                    }

                    return (
                        <Box key={module.id} component="span" sx={{ display: "block" }}>
                            {collapsedNavTooltip(
                                module.label,
                                true,
                                <ListItemButton
                                    selected={isActive}
                                    className="app-shell__navButton app-shell__navButton--collapsed"
                                    onClick={() => onNavigate(firstHref)}
                                    sx={{
                                        borderRadius: 2,
                                        minHeight: 48,
                                        width: "100%",
                                        minWidth: 0,
                                        px: 0.75,
                                        py: 1,
                                        justifyContent: "center",
                                        borderLeft: "none",
                                        bgcolor: isActive ? moduleTheme.accentSoft : moduleTheme.accentMuted,
                                        boxShadow: isActive ? `inset 0 0 0 2px ${moduleTheme.accentBorder}` : "none",
                                    }}
                                >
                                    <Box
                                        className="app-shell__navIcon app-shell__navIcon--collapsed"
                                        sx={moduleNavIconSx(module.id)}
                                    >
                                        <ModuleIcon size={18} />
                                    </Box>
                                </ListItemButton>,
                            )}
                        </Box>
                    );
                })}
            </>
        );
    }

    return (
        <>
            {visibleModules.map((module) => {
                const ModuleIcon = moduleNavIcons[module.id];
                const moduleTheme = moduleNavThemes[module.id];
                const isExpanded = expandedModules.has(module.id);
                const isActiveModule = module.id === activeModuleId;

                return (
                    <Box key={module.id} sx={{ display: "grid", gap: 0.5 }}>
                        <ListItemButton
                            className="app-shell__navButton app-shell__navButton--module"
                            selected={isActiveModule}
                            onClick={() => toggleModule(module.id)}
                            sx={{
                                borderRadius: 2,
                                minHeight: 48,
                                px: 2,
                                gap: 1.25,
                                ...moduleNavHeaderSx(module.id, isActiveModule),
                            }}
                        >
                            <Box className="app-shell__navIcon" sx={moduleNavIconSx(module.id)}>
                                <ModuleIcon size={18} />
                            </Box>
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    flex: 1,
                                    color: isActiveModule ? moduleTheme.iconColor : "text.primary",
                                }}
                            >
                                {module.label}
                            </Typography>
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </ListItemButton>

                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <List
                                disablePadding
                                sx={{
                                    display: "grid",
                                    gap: 0.5,
                                    pl: 0.5,
                                    borderLeft: `2px solid ${moduleTheme.accentBorder}`,
                                    ml: 1.25,
                                }}
                            >
                                {module.children.map((leaf) => renderNavNode(leaf))}
                            </List>
                        </Collapse>
                    </Box>
                );
            })}
        </>
    );
}
